'use client';

import { blockKey } from '@hyphengroup/cms-components/calculatedAttributes/blockKey';
import { SingSaverRewardsSectionId } from '@hyphengroup/cms-components/cms-blocks/ComparisonTable/constants';
import { QuickAnswer } from '@hyphengroup/cms-components/cms-blocks/QuickAnswer';
import type {
  OnQuickAnswerProductClick,
  OnQuickAnswerRewardCellClick,
  QuickAnswerProps,
} from '@hyphengroup/cms-components/cms-blocks/QuickAnswer';
import RewardAsideModal from '@hyphengroup/cms-components/components/RewardAsideModal/RewardAsideModal';
import useOpenRewardModal from '@hyphengroup/cms-components/hooks/useOpenRewardModal';
import {
  ComponentTypeEnum,
  sendProductAnalytics,
  UserActionEnum,
} from '@hyphengroup/cms-components/utils/analytics/sendAnalytics';
import { filterOffersByAmountAndTenure } from '@hyphengroup/cms-components/utils/helpers/offers';
import { useMemo } from 'react';
import { useModuleQuotesPoll } from '../../helpers/useModuleQuotesPoll';
import { pushDataLayer } from '../../pushDataLayer';
import {
  useApprovalOdds,
  useApprovalOddsRetrieval,
} from '$/(cms)/_modules/approvalOdds';
import { usePageDataContext } from '$/(cms)/_modules/cmsService/pageDataService/pageDataProvider';
import { useProductDetailModal } from '$/components/cms/helpers/useProductDetailModal';
import { useClientContext } from '$/modules/context/client';

export type QuickAnswerWrapperProps = Omit<
  QuickAnswerProps,
  'onProductClick' | 'onRewardCellClick' | 'pushDataLayer'
> & {
  /** Used by the wrapper to build the reward modal key; not forwarded to QuickAnswer. */
  blockIndex?: number;
};

/**
 * Client wrapper for QuickAnswer. Mirrors ComparisonTableWrapper:
 *  - clicking a product name opens the product aside modal (if url_alias).
 *  - clicking a reward cell opens RewardAsideModal (PL with offers) or
 *    the product aside scrolled to the reward section.
 *
 * Defensive: renders nothing on money101 / moneymax (per CMS-1584 — SS + MH only).
 */
export function QuickAnswerWrapper(props: QuickAnswerWrapperProps) {
  const { brand, langPrefix } = useClientContext();
  const { products, vertical, blockIndex, slug, baseColumnOrders, ...rest } =
    props;

  const { openModal: openRewardModal } = useOpenRewardModal();
  // "_pdp" keeps the key distinct from the per-product RewardAsideModal below,
  // which shares the same rewardModal hash (CMS-1924).
  const { openProductDetail, productDetailModal } = useProductDetailModal({
    componentUniqueKey: `QuickAnswer_${blockIndex ?? 0}_pdp`,
  });
  const { publicCmsConfig } = usePageDataContext();

  // Continue an SSR-started quote poll for THIS module, if one is unfinished:
  // each tick lands in the page store and re-renders the table, filling the
  // loading price cells in (CMS-1956 R4).
  // One key for both the poll and the lookup, so they cannot disagree: a
  // truthy gate on the lookup alone would leave the poll writing under `''`
  // while the wrapper read nothing and silently fell back to the page quote.
  // Hooks run unconditionally, and a module whose id is missing emitted no
  // request under this key either, so the lookup simply misses.
  const moduleId = blockKey(rest);
  useModuleQuotesPoll(moduleId);

  // A Personal Loan module's reward modal describes the module's own loan: its
  // tiers and its Apply CTA, as the cells do (CMS-1928 R1/R4). Without both
  // inputs there is no loan, and the modal stays as for any other module.
  const isLoanModule = rest.mode === 'PERSONAL_LOAN';
  const { loanAmount, loanTenure } = rest;
  const moduleLoanSearchParams = useMemo(
    () =>
      isLoanModule && loanAmount && loanTenure
        ? { amount: loanAmount.toString(), tenure: loanTenure.toString() }
        : undefined,
    [isLoanModule, loanAmount, loanTenure],
  );
  const hasRewardModal = vertical === 'PERSONAL_LOANS' || isLoanModule;

  const isSupportedBrand = brand === 'singsaver' || brand === 'moneyhero';
  const hasApprovalOddsColumn = !!baseColumnOrders?.some(
    (col) => col.keyColumn === 'APPROVAL_ODDS',
  );
  const { data: approvalOddsData, sendConsent } = useApprovalOdds(
    products,
    isSupportedBrand && hasApprovalOddsColumn,
    langPrefix,
  );
  const { onRetrieveApprovalOdds, oddsConsentModal } = useApprovalOddsRetrieval(
    {
      approvalOddsData,
      sendConsent,
      langPrefix,
      chcBaseUrl: publicCmsConfig.chcBaseUrl,
    },
  );

  if (!isSupportedBrand) {
    return null;
  }

  const rewardModalComponentKey = `QuickAnswer_${blockIndex ?? 0}`;

  const onProductClick: OnQuickAnswerProductClick = (product, rowIndex) => {
    if (!product.url_alias) return;
    sendProductAnalytics({
      action: UserActionEnum.CLICK,
      componentTitle: 'Product List/Quick Answer_See all Details',
      componentType: ComponentTypeEnum.TABLE,
      event: 'product_interaction',
      index: rowIndex.toString(),
      product,
      url: slug,
      pushDataLayer,
    });
    openProductDetail(product, null);
  };

  const onRewardCellClick: OnQuickAnswerRewardCellClick = (
    e,
    product,
    rowIndex,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    sendProductAnalytics({
      action: UserActionEnum.CLICK,
      componentTitle: 'Product List/QuickAnswer_Reward',
      componentType: ComponentTypeEnum.TABLE,
      event: 'sendAnalytics',
      index: rowIndex.toString(),
      product,
      url: slug,
      pushDataLayer,
    });

    if (
      hasRewardModal &&
      (product.welcome_offers?.length || product.exclusive_offers?.length)
    ) {
      openRewardModal(product.uid ?? '', rewardModalComponentKey);
      return;
    }

    openProductDetail(product, SingSaverRewardsSectionId);
  };

  return (
    <>
      <QuickAnswer
        {...rest}
        products={products}
        vertical={vertical}
        slug={slug}
        baseColumnOrders={baseColumnOrders}
        approvalOddsData={approvalOddsData ?? undefined}
        onApprovalOddsUnlockClick={onRetrieveApprovalOdds}
        onProductClick={onProductClick}
        onRewardCellClick={onRewardCellClick}
        pushDataLayer={pushDataLayer}
      />
      {hasApprovalOddsColumn ? oddsConsentModal : null}
      {productDetailModal}
      {hasRewardModal
        ? products
            .filter(
              (p, i) =>
                products.findIndex((p2) => p2.uid === p.uid) === i &&
                (p.welcome_offers?.length || p.exclusive_offers?.length),
            )
            .map((product) => {
              const welcomeOffers = filterOffersByAmountAndTenure(
                product.welcome_offers ?? [],
                moduleLoanSearchParams?.amount,
                moduleLoanSearchParams?.tenure,
              );
              const exclusiveOffers = filterOffersByAmountAndTenure(
                product.exclusive_offers ?? [],
                moduleLoanSearchParams?.amount,
                moduleLoanSearchParams?.tenure,
              );
              return (
                <div key={product.uid}>
                  <RewardAsideModal
                    welcomeOffers={welcomeOffers}
                    exclusiveOffers={exclusiveOffers}
                    loanSearchParams={moduleLoanSearchParams}
                    product={product}
                    keyFromComponent={rewardModalComponentKey}
                    vertical={isLoanModule ? 'PERSONAL_LOANS' : vertical}
                    pushDataLayer={pushDataLayer}
                    ctaLinkOverride={undefined}
                    carInsuranceCtaComponent={null}
                    priceLine={null}
                  />
                </div>
              );
            })
        : null}
    </>
  );
}
